import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BoardColumnCoreFragment } from '../../../generated/graphql';
import type { ColumnMoveArgs } from './useBoardDragHandlers';
import { dragTrace } from './drag-trace';

type Column = BoardColumnCoreFragment;

/**
 * The id order produced by moving `columnId` to sit after `afterColumnId`
 * (or to the head when `afterColumnId` is undefined). Pure — mirrors the
 * anchor semantics `moveColumnInBoardCache` applies to positions.
 */
export function orderAfterMove(
  ids: readonly string[],
  columnId: string,
  afterColumnId: string | undefined
): string[] {
  const without = ids.filter((id) => id !== columnId);
  if (afterColumnId === undefined) return [columnId, ...without];
  const anchor = without.indexOf(afterColumnId);
  if (anchor < 0) return [...ids]; // unknown anchor — leave the order alone
  without.splice(anchor + 1, 0, columnId);
  return without;
}

const sameOrder = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Holds the column order across a drop.
 *
 * Column moves commit through Apollo, and until that write produces a render
 * the board still renders the OLD order — so dnd-kit animates every shifted
 * column back to its original slot and the board only settles a frame (or a
 * network round-trip) later. That is the "snaps back, then updates" symptom.
 *
 * dnd-kit's own sortable guidance is to update the items array synchronously
 * in onDragEnd. This hook is that step: `applyMove` records the resulting
 * order immediately, `displayColumns` renders it, and the pending order is
 * dropped once the live data agrees — which also covers a failed mutation
 * rolling back, since the rollback simply never matches and the next live
 * order wins.
 *
 * Deliberately shaped like `useBoardCardSlotting` so the two read alike:
 * a transient view over live data, cleared when it is no longer needed.
 */
export function useBoardColumnOrder({ columns }: { columns: Column[] }) {
  // `baseline` is the live order at the moment of the drop. The pending view
  // is released as soon as live data moves off it — see the effect below.
  const [pending, setPending] = useState<{
    order: string[];
    baseline: string[];
  } | null>(null);

  const applyMove = useCallback(
    ({ columnId, afterColumnId }: ColumnMoveArgs) => {
      const baseline = columns.map((c) => c._id);
      setPending({
        order: orderAfterMove(baseline, columnId, afterColumnId),
        baseline,
      });
    },
    [columns]
  );

  const clear = useCallback(() => setPending(null), []);

  const liveIds = useMemo(() => columns.map((c) => c._id), [columns]);

  // Release on ANY change to live data, not on a match with the pending
  // order. Matching sounds tighter but strands the view when the order never
  // arrives: a failed mutation rolls the cache back to the baseline, which
  // would never equal the pending order, and the board would be stuck showing
  // a move the server rejected. Any movement means the cache has spoken —
  // whether it agreed with us, rolled back, or changed for another reason —
  // and live data should win from there.
  useEffect(() => {
    if (pending && !sameOrder(liveIds, pending.baseline)) {
      dragTrace('reconc', { live: liveIds.join('>') });
      setPending(null);
    }
  }, [liveIds, pending]);

  const displayColumns = useMemo(() => {
    if (!pending) return columns;
    const byId = new Map(columns.map((c) => [c._id, c]));
    const next: Column[] = [];
    const placed = new Set<string>();
    for (const id of pending.order) {
      const column = byId.get(id);
      if (column) {
        next.push(column);
        placed.add(id);
      }
    }
    // Anything live but absent from the pending order still renders — this
    // view must never drop or duplicate a column, even for the single frame
    // before the effect above releases it.
    for (const column of columns) {
      if (!placed.has(column._id)) next.push(column);
    }
    return next;
  }, [columns, pending]);

  return { displayColumns, applyMove, onDragCancel: clear };
}
